<template>
  <div>
    <h2>The Virginia Ranger Companies, 1755- 1763</h2>
    <p>
      During this period, the western settlements of the colony of Virginia were
      subject to Native American raids, initially as part of the French and
      Indian War, then Pontiac Rebellion and finally war with the Cherokees. The
      colony of Virginia raised companies of Rangers to assist in the defense of
      its vast western territories. The Virginia Regiment and Militia were the
      other military organizations participating in the defense of western
      Virginia.
      <br>
      <br>The Ranger Companies were raised on an annual basis and subject to
      disbandment or continuation, situation and budget dependent. They were
      typically raised in specific counties for service in that county. There
      are at least two exceptions to this, however, the expedition to the Ohio
      River and the expedition to the Cherokees, both in 1756 and both conducted
      in whole or part with Ranger Companies. Raising men for local service
      meant that they probably had some familiarity with settlements, forts and
      terrain in their area of operations. Their degree of familiarity would
      have been far greater than that of militia sent into western counties for
      brief periods of service and the Virginia Regiment which was raised from
      all the counties of Virginia.
      <br>
      <br>The men were paid a daily rate with arms provided by the colony, if
      required. There was clothing provided to at least two of the Companies in
      1755, with consequent stoppages or deductions in pay. Clothing was
      apparently provided by Lord Fairfax on one occasion in 1756 as well. There
      is no reason to believe the clothing provided was the blue uniform with
      red facings worn by the Virginia Regiment. There is no record of any
      attempt on the part of the Colony of Virginia or of the Ranger Company
      Commanders to provide any sort of distinctive uniform or insignia to the
      Ranger Companies. There was an active market for used clothing in Britain
      and her Colonies in the 18th century and that is the likely sources for
      clothing provided to the Ranger Companies in 1755. After 1755, it was
      specifically stated that the men were responsible for providing their own
      clothing. This being the case, men of the Ranger Companies would have worn
      the civilian clothing of the period.
      <br>
      <br>The planned strength for the Ranger Companies was 100. Actual
      Company strength, based on existing accounts, ranged from as many as 60
      men to as few as 10. There is no way of knowing the company strengths when
      raised to see if any of them actually enlisted 100 men and no way to
      determine losses over time to illness, death and desertion. All we are
      left with is numerical snap shots and the occasional disparaging remarks
      from Governor Dinwiddie and Colonel Washington about absence and
      desertion.
      <br>
      <br>There was a failed attempt to incorporate the Ranger Companies into
      the Virginia Regiment in 1756. It seems the terms of service, and very
      likely the discipline, were the reason the Ranger Companies declined the
      request. It was not until 1757 and the ‘Act for Preventing Mutiny and
      Desertion’ that the Ranger Companies and the Virginia Regiment were
      subject to the same military law. This did not seem to prevent the raising
      of Ranger Companies and there is no way of knowing if it had an effect on
      recruitment for those companies raised.
      <br>
      <br>Like all military organizations of the 18th century, women had a
      critical logistical role. Women would have been present on forts,
      blockhouses and settlements across western Virginia. The women in those
      settlements would have repaired and altered clothing as necessary, cooked
      meals and provided medical care to the sick and wounded. They were even
      known to have participated in the defense of their homes. There would have
      no doubt been children present as well.
    </p>
    <h3>Periods of Service and County</h3>
    <p>
      <b>14 July 1755 - 4 May 1759</b> CPT William Preston’s (Augusta County)
      <br>
      <br>
      <b>14 July 1755 - 1758</b> CPT John Ashby’s (Frederick County)
      <br>
      <br>
      <b>14 July 1755 - 24 June 1756</b> CPT William Cock’s (Frederick County)
      <br>
      <br>
      <b>14 August 1755 -? 1755</b> CPT Nathaniel Terry’s (Amelia County)
      <br>
      <br>
      <b>14 August 1755 - September 1756</b> CPT Samuel Overton's (Hannover
      County)
      <br>
      <br>
      <b>20 August 1755 -? 1755</b> CPT John Phelps’ (Bedford County)
      <br>
      <br>
      <b>September 1755 - March 1756</b> CPT John Smith’s (Augusta County)
      <br>
      <br>
      <b>September 1755 -? 1755</b> CPT Henry Anderson’s (Amelia County)
      <br>
      <br>
      <b>August 1756</b> CPT George Wilson’s (?)
      <br>
      <br>
      <b>8 September 1756 - December 1756</b> CPT Samuel Stahlnaker’s (?)
      <br>
      <br>
      <b>24 November 1754 - 1758</b> CPT Peter Hogg’s (Augusta County)
      <br>
      <br>
      <b>24 November 1756 - 1759</b> CPT John Dickenson (Augusta County)
      <br>
      <br>
      <b>1757</b> CPT William Goodrich’s (?)
      <br>
      <br>
      <b>1758 - 59</b> CPT Robert Rutherford’s (Frederick County)
      <br>
      <br>
      <b>1758</b> CPT John Dunlop’s (?)
      <br>
      <br>
      <b>1758 (3 months)</b> CPT Alexander Sayer’s (?)
      <br>
      <br>
      <b>February 1759 - February 1760</b> LTC William Peachy’s Frontier
      Battalion
      <br>
      <br>
      <b>1760 - 64</b> CPT William Christian’s (?)
      <br>
      <br>
      <b>1763</b> CPT William Phillip’s (?)
    </p>
    <h3>Chronology and Sources</h3>
    <p>
      <b>14 February 1755 – 1757</b> CPT William Preston commands a Company of
      Rangers in Augusta County
    </p>
    <blockquote>
      “A sum of money not to exceed two thousand pounds, to be laid out for and
      in the raising and maintaining three companies of men, consisting of fifty
      men each, with their officers, to be employed as rangers, for the
      protection of the subjects in the frontiers of this colony, as the
      governor shall direct from time to time, and shall not be sent out of this
      colony, nor incorporated with the soldiers now in his majesty’s service,
      or made subject to martial law. And in case the said number of men, cannot
      be raised, by such as will voluntarily enlist in the said service, it
      shall and may be lawful for the county lieutenant or chief officer of the
      militia of each of the counties of Frederick, Hampshire, and Augusta, by
      direction from the governor to draft out to the militia, of the said
      counties, respectively such and so many young men of their militia who
      have not wives or children, as will make up the said number.”
    </blockquote>
    <h6>Laws of Virginia, May 1755</h6>
    <p>
      In July of 1755, GOV Dinwiddie called up 3 Ranger Companies that remained
      in service from 14 July 1755 to 24 June 1756. They were CPT John Ashby’s
      Company of Rangers (Hampshire County), CPT William Preston’s Company of
      Rangers (Augusta County) and CPT William Cocks Company of Rangers
      (Frederick County). On 30 July CPT Andrew Lewis was appointed as
      replacement for COL James Patton, County Lieutenant of Augusta County and
      remained in command of an “independent company” of the Virginia Regiment
      until 1756.
      <br>
      <br>14 August 1755 The number of Ranger Companies was increased with CPT
      Nathaniel Terry’s Company of Rangers (Amelia County) and CPT Samuel
      Overton's Ranger Company (Hannover County). The literature indicates that
      CPT Overton’s Company remained in service until 25 October 1755. CPT John
      Phelps’ Company of Rangers (Bedford County) and CPT Nathaniel Terry
      (Halifax County) entered service on 20 August 1755.
    </p>
    <p>
      <b>September 1755</b> Ranger Companies in service on the frontier:
      <br>
      <span class="padding">CPT John Smith’s (40 men) (Augusta County)</span>
      <br>
      <span class="padding">CPT William Preston’s (40 men) (Augusta County)</span>
      <br>
      <span class="padding">CPT Henry Anderson (Amelia County)</span>
      <br>
      <span class="padding">CPT John Ashby’s (30 men) (Hampshire County)</span>
      <br>
      <span class="padding">CPT Samuel Overton’s (14 men) (Hannover County)</span>
      <br>
      <span class="padding">CPT William Cocks (30 men) (Frederick County)</span>
      <br>
      <span class="padding">CPT John Phelps (Bedford County)</span>
      <br>
      <span class="padding">CPT Nathaniel Terry (Halifax County)</span>
    </p>
    <blockquote>
      “I received your letter of the 9th and I approve of your intentions to
      range the woods with the detachments from three companies and by no means
      continue in one place, but to proceed wherever you think the Indians may
      come to annoy our back settlements and I expect a number of the Cherokees
      will be with you this winter; if they do come, give orders that they may
      be kindly used. I have now sent up to COL Steuart 200 L for the use of the
      Rangers. And I think your country should do as some others have done to
      contribute to get your men some clothing. Your Rangers are to be paid 8 d.
      per day, you may make a stoppage of 2d. per day for their clothing.”
    </blockquote>
    <h6>LT GOV Dinwiddie to CPT William Preston, 15 September 1755</h6>
    <blockquote>
      “Whereas it has been represented to me, that many of the inhabitants of
      the County of Augusta, have most shamefully deserted their plantations for
      fear of an enemy, which a small number of them, with a proper spirit,
      might easily have destroyed, and that they are still so afraid, as to
      think of leaving everything that they have, and seek for settlements in
      some other country; I thought it might not be amiss to inform them, in
      this public manner, that they may return to their estates with the utmost
      security; that the five Companies of Rangers, and an independent company,
      consisting of such as have voluntarily offered to assist in defending the
      frontiers, are stationed in the best manner to provide for their security;
      and that the men under Col. Washington, will all be disposed of, so as to
      answer the same purpose.”
    </blockquote>
    <h6>LT GOV Robert Dinwiddie, Virginia Gazette, 26 September 1755</h6>
    <blockquote>
      “The Indians discover our parties by the track of their shoes. It would be
      a good thing to have shoe-packs or moccasins for the scouts.”
    </blockquote>
    <h6>COL Adam Stephen to COL Washington, 27 September 1755</h6>
    <p>
      <b>October 1755</b> Fort William/Fort Preston (Haymakertown, VA built by
      CPT William Preston and Ranger Company (16 men).
    </p>
    <blockquote>
      “From Augusta we learn that a party of the militia, under the command of
      one Captain Dickenson, met with and attacked a party of northern Indians,
      of whom one was killed and several wounded. Two Indian boys, of the
      Catawba Nation, whom the northern Indians had taken prisoner, were retaken
      by our people. Captain Dickenson had one man killed.”
    </blockquote>
    <h6>Virginia Gazette, 3 October 1755</h6>
    <blockquote>
      “I doubt not you will be encouraged by this to make a stand, in case you
      are attacked or besieged; as I hope very quickly to relieve you, and make
      the Savages and French (who are no better) pay for their presumption. If
      their numbers are not large, from many concurring accounts, you ought to
      send out Parties to stop their progress, which the timidity of the
      Inhabitants has been the cause of. If it should so happen, that you are
      obliged to quit your Fort for want of provisions, &c. you are hereby
      positively Ordered, to retreat no farther than Joseph Edwards on
      Cacapehon"
    </blockquote>
    <h6>
      COL George Washington at Winchester
      <br>to CPTs William Cocke and John Ashby (Ranger Company Commanders),
      <br>October 10, 1755
    </h6>
    <blockquote>
      “It is my express Orders, that you do not presume to March your Company
      down on any pretence whatsoever, unless compelled by the Enemy. Clothes
      will be sent up immediately to you, which you may distribute to the most
      needy of your Company; and Money I shall bring up to pay them off, if
      wanted.”
    </blockquote>
    <h6>
      COL Washington
      <br>to CPT John Ashby at Winchester,
      <br>14 October, 1755
    </h6>
    <blockquote>
      “The money you had before is to be entirely applied for the Rangers, and
      if you have supplied them with any clothes please stop out of their pay
      50s. for each suit.”
    </blockquote>
    <h6>
      GOV Dinwiddie to COL George Fairfax,
      <br>18 October 1755
    </h6>
    <p>
      On 21 Oct 1755 CPT John Ashby’s 2nd Company of Rangers reported strength
      was 33 men. CPT William Cock’s 1st Company of Rangers reported strength
      was 24 Men.
    </p>
    <blockquote>
      “You are hereby ordered, to remain with your Companies at George Parkers'
      Plantation, where you are to erect a Stockade Fort; in building of which,
      you are to follow Lieutenant Bacons Instructions; he being sent to direct
      and plan the same: As this is intended for the protection of the Country
      People, there is no doubt but they will assist all in their power,
      especially in providing Tools which, without, you may meet with some
      difficulty. If Lieutenant Bacon should apply for an Escort to conduct him
      to Captain Ashby's Company, or to any other place, to which it may be
      dangerous travelling without; you are to see that he is allowed it: You
      are to send a trusty Sergeant with proper powers &c. for Recruiting, in
      order to complete your Company"
    </blockquote>
    <h6>
      COL George Washington to Ranger Company Commanders,
      <br>Captains William Cocke and John Ashby,
      <br>October 27, 1755.
    </h6>
    <blockquote>
      “You are hereby ordered, to remain with your Companies at George Parkers'
      Plantation, where you are to erect a Stockade Fort; in building of which,
      you are to follow Lieutenant Bacons Instructions; he being sent to direct
      and plan the same: As this is intended for the protection of the Country
      People, there is no doubt but they will assist all in their power,
      especially in providing Tools which, without, you may meet with some
      difficulty. If Lieutenant Bacon should apply for an Escort to conduct him
      to Captain Ashby's Company, or to any other place, to which it may be
      dangerous travelling without; you are to see that he is allowed it: You
      are to send a trusty Sergeant with proper powers &c. for Recruiting, in
      order to complete your Company.
    </blockquote>
    <h6>
      COL George Washington to Ranger Company Commanders,
      <br>Captains William Cocke and John Ashby,
      <br>October 27, 1755.
    </h6>
    <p>
      <b>December 1755</b> Ft. Ashby constructed on Patterson’s Creek (Fort
      Ashby, WV) and garrisoned by CTP Ashby’s Company of Rangers.
    </p>
    <blockquote>
      “In pursuance of Commands from the Governor to me, you are hereby ordered
      to proceed to Augusta, and there to take upon you the command of Captain
      Hogg's Company; three Companies of Rangers, and such Cherokee Indians as
      you shall find there; and march them to such place or places, and obey
      such order or orders, as the Governor shall direct.”
    </blockquote>
    <h6>
      COL Washington at Winchester to MAJ Andrew Lewis,
      <br>27 December, 1755.
    </h6>
    <blockquote>
      “You are hereby ordered to repair to your Company with all possible
      dispatch. I have found it impracticable to procure Clothes for your men. I
      think none so proper for Rangers as matchcoats; therefore would advise you
      to procure them. Those who have not received Clothing; for the future will
      have their full pay, without stoppages; and those already made, will be
      repaid to them. Those who have been clothed, must either return them, or
      allow stoppages. I would have you consult your men, and fall upon some
      method to supply them immediately. You will receive a Bill against Mutiny
      and Desertion, which you must have often read to the men. And further
      assure them that if any Soldier deserts, although he return himself, he
      shall be hanged.”
    </blockquote>
    <h6>
      COL Washington to CPT William Cocke at Winchester,
      <br>28 December, 1755
    </h6>
    <blockquote>
      “I am very much surprised to hear the great irregularities which were
      allowed of in your Camp. The Rum, although sold by Joseph Coombs, I am
      credibly informed, is your property. There are continual complaints to me
      of the misbehavior of your Wife; who I am told sows sedition among the
      men, and is chief of every mutiny. If she is not immediately sent from the
      Camp, or I hear any more complaints of such irregular behavior upon my
      arrival there; I shall take care to drive her out myself, and suspend you.
      It is impossible to get clothing here for your men. I think none so proper
      for Rangers as Match-coats; therefore would advise you to procure them.
      Those who have not received clothing, for the future will receive their
      full pay without stoppages; and those already made, will be repaid them.
      Those who have been clothed must either return them or allow stoppages. “
    </blockquote>
    <h6>
      COL Washington to CPT John Ashby at Winchester,
      <br>28 December, 1755.
    </h6>
    <blockquote>
      “You are hereby ordered to repair to the company immediately and use your
      utmost endeavors to keep due regulation, until the return of Captain
      Ashby. So soon as you arrive there you are to acquaint Captain Lewis it is
      my Orders that he with his party return to Fort Cumberland.”
    </blockquote>
    <h6>
      COL Washington to Lieutenant Thomas Rutherford,
      <br>Second Company of Rangers,
      <br>29 December 1755
    </h6>
    <p>
      <b>29 December 1755</b> Returns for CPT Thomas Cock’s Company at Parkers
      Plantation: I CPT, I LT, 25 men present, 1 absent, and 1 sick.
    </p>
    <blockquote>
      "As to those fifty suits delivered Colonel Fairfax for the Rangers I have
      no cognizance of them; they were delivered by himself, Colonel Martin,
      Lord Fairfax, and the Officers of the said Rangers with the greatest
      irregularity; as indeed some other of the public Stores have been by their
      order's; such as ammunition &c."
    </blockquote>
    <h6>
      COL Washington to LT GOV Dinwiddie at Alexandria,
      <br>2 February, 1756.
    </h6>
    <p>
      <b>18 February - 13 March 1756.</b> After her return from captivity, in
      November of 1755, Mary Ingles provided her husband, CPT William Ingles,
      with intelligence about Shawnee villages along the Ohio. In December of
      1755, CPT Ingles approached GOV Dinwiddie about conducting a raid against
      two of these villages near present day Portsmouth, OH. MAJ Andrew Lewis
      was designated to lead the expedition of approximately 200-300 men from
      the Virginia Regiment and Militia Rangers along with approximately 80-130
      Cherokees. Captain Hogg was ordered to provide and lead forty men from his
      Company. A draft of sixty men from Captain William Preston’s and Captain
      John Smith’s Company, to be commanded by Captain Smith was ordered to
      participate. Captain Overton and Captain Obadiah Woodson were to provide
      forty men each and serve as Company Commanders. Captain Pearis commanded
      the Cherokee’s. Captain Robert Breckinridge, was ordered to take his
      companies as well. Captain’s Archibald Alexander, John Montgomery and
      Dunlap commanded volunteer companies of indeterminate size. Captain David
      Stuart served as commissary. CPT William Ingles, the instigator if not,
      perhaps, the planner of the mission, joined the expedition as well. The
      main body of the expedition departed Fort Frederick (vicinity Salem, VA)
      on 18 February 1756. The expedition with the exception of CPT Hogg’s men
      who were behind the main body, reached the north Fork of the Holston River
      by 23 February. By 26 February the expedition was at the head of the
      Clinch River and at the head of Sandy Creek by the 28th which they
      followed towards the Ohio River until desertion and insubordination caused
      the expedition to turn around on 13 March.
    </p>
    <p>
      <b>Spring 1756</b>
      Attack on Neally’s Fort (on the Opequon River). All occupants killed.
      <br>
      <br>
      <b>Spring 1756</b> Battle of the Trough at Fort Pleasant/Fort Henry Van
      Meter. Party of 16 men departs Ft Pleasant in pursuit of Indian Party.
      Seven KIA and 4 WIA.
      <br>
      <br>
      <b>29 March 1756</b> LTC Stephen reports to COL Washington that CPT Cox’
      Company of Rangers has 38 men and CPT Ashby’s Company of Rangers has 40
      men.
      <br>
      <br>
      <b>April 1756</b> Battle of Lost Spring (vicinity head of the Capon
      River). CPT Jeremiah Smith and 23 Frederick County Militiamen defeat 50
      French and Indians who disperse and flee. 2 militia KIA 1 Frenchman and 5
      Indian KIA. Plan discovered for link up with party of 50 to attack Ft.
      Frederick.
      <br>
      <br>
      <b>18 April, 1756</b> The Battle of Great Cacapon. CPT John Mercer and 60
      men garrisoning Fort Edward ambushed while in pursuit of hostile Indians
      near Fort Edward. CPT Mercer and 54 men KIA.
    </p>
    <blockquote>
      “Sir: You are hereby Ordered to repair to Joseph Edward's Fort, and there
      to take upon you the command of all those different parties that are at
      that place. You are to use your utmost endeavors to protect the people,
      and be very circumspect in your conduct; taking care to do nothing without
      first advising with your Officers, and receiving their Counsel. You are to
      be very careful that you are not decoyed into any snares of the Enemy: And
      if you ever detach any parties from the Fort, be sure to cover their
      retreat; and, if possible, draw them between your Fires, by advancing a
      Body of men before your main Body; with Orders to retreat gradually
      between your parties, which you must have posted securely for that
      purpose. You must be very careful that no waste is made of the Ammunition;
      and that the men are not allowed to make random shot; but watch their
      opportunities to fire sure. You are to be careful in transmitting me
      constant reports of the occurrences that may attend while there: and are
      to endeavor to keep a communication with the Detachment at Enoch's. You
      are also, by any opportunities that shall offer, to send word to all such
      places as have men stationed; and to warn the Inhabitants to be on their
      Guard.”
    </blockquote>
    <h6>
      COL George Washington at Winchester to CPT Henry Harrison,
      <br>April 19, 1756.
    </h6>
    <blockquote>
      “I can scarcely give credit to any part of the Report you transmitted to
      me, from Captain Ashby. If Captain Harrison can, by good woods-men, get
      intelligence of the number of the Enemy, and their place of Rendezvous, if
      near your Station. I would have them endeavor to surprise them in the
      night, by failing upon them at their sleeping places”
    </blockquote>
    <h6>COL Washington to LT William Stark 20 April, 1756.</h6>
    <blockquote>
      “Gentlemen: I have just now received several Expresses who bring the most
      shocking accounts of the distressed condition, not only of the few poor
      families that yet remain back of this place; but of the Rangers that
      Garrison the small Forts: as nothing but a large and speedy reinforcement
      can save them from utter destruction! I must desire that you will not lose
      one moment in drawing together all the men you can provide with Arms,
      Ammunition, and Provision, in the best manner the time will admit of; and
      join me with all imaginable expedition..”
    </blockquote>
    <h6>
      COL Washington to the Commanding Officers of Prince William and Fairfax
      Militia,
      <br>April 21, 1756.
    </h6>
    <blockquote>
      “A small fort which we have at the mouth of Patterson’s Creek, containing
      an officer and thirty men now guarding stores, was attacked suddenly by
      French and Indians; they were warmly received, upon which they retired.”
    </blockquote>
    <h6>COL Washington to LT GOV Dinwiddie, 22 April 1756</h6>
    <p>
      CPT John Ashby wrote from his fort (April 15) that 400 Indians had
      demanded surrender of his fort; 1,500 had gone to Fort Cumberland and
      2,000 to the Juniata.
    </p>
    <blockquote>
      “I do promise and engage to all good Woodsmen, &c. who will enter into the
      Service of their Country now, for a month or longer; if they will subject
      themselves to Military discipline, for the time they engage and undertake
      to do Soldiers Duty, and obey my Orders; That they shall receive Soldiers'
      pay, ammunition and Provision; and be discharged at the time agreed on.”
    </blockquote>
    <h6>Winchester Advertisement, 22 April, 1756</h6>
    <p>
      <b>23 April 1756</b> Battle of the Trough. Militia from Ft Hopewell
      looking for Indians is ambushed while traversing the low ground in “The
      Trough”.
    </p>
    <blockquote>
      “Three families were murdered the night before last, at the distance of
      less than twelve miles from this place; and every day we have accounts of
      such cruelties and barbarities as are shocking to human nature. It is not
      possible to conceive the situation and danger of this miserable country.
      Such numbers of French and Indians are all around, that no road is safe;
      and here we know not the hour when we may be attacked.”
    </blockquote>
    <h6>COL Washington at Winchester to LT GOV Dinwiddie, 24 April 1756</h6>
    <p>
      <b>24 April 1756</b> MAJ Andrew Lewis and CPT Overton’s Ranger Company of
      60 men proceed to the Cherokee country to construct what was to become
      Fort Loudon at the junction of the Tellico and Little Tennessee Rivers.
      <br>
      <br>
      <b>24 April 1756</b> LT GOV Dinwiddie orders CPT William Preston to draft
      1/3 of the Augusta County Militia into service to replace CPT Overton’s
      Company.
    </p>
    <blockquote>
      “Captain Baylis, of the Prince William Militia, will give you this; and
      leave you a reinforcement of twenty men; with these, and the Detachment of
      your own Company, which has now certainly rejoined you; you will be
      sufficiently able to send out several scouting parties: And it is my
      desire, you do your utmost to scour these parts, and protect the people.
      You will deliver Mr. Baylis what Carpenters tools you have in the Fort: as
      he has Orders to build a small Fort at the mouth of Little Capecapon. Take
      Receipts for the several Tools you deliver the Officers. I am, &c.”
    </blockquote>
    <h6>
      COL Washington to CPT William Cocke,
      <br>(1st Company of Rangers),
      <br>12 May, 1756.
    </h6>
    <p>
      August 1756 LT Thomas Rutherford of Ashby’s Company and his Rangers
      supported by militia escorting supplies from Winchester, VA to Ft.
      Cumberland were forced to return to Winchester after the attached militia
      fled when alerted to a possible Indian attack.
    </p>
    <blockquote>
      “I make no doubt, that your Honor has ere heard of the defeat of
      Lieutenant Rutherford of the Rangers, escorting an express to me at Fort
      Cumberland, and of the dastardly behavior of the militia, who ran off
      without one half of them having discharged their pieces, although they
      were appraised of the ambuscade by one of the flanking parties, before the
      Indians fired upon them; and ran back to Ashby’s Fort, contrary to orders,
      persuasions, threats, &.”
    </blockquote>
    <h6>COL Washington to LT GOV Dinwiddie, 4 August 1756</h6>
    <p>
      <b>August 1756</b> CPT George Wilson’s Ranger Company reported strength of
      50 men.
    </p>
    <blockquote>
      “The incorporating of the Rangers in the Regiment will be very agreeable,
      if done with their consent, and I hope by arguments you may prevail on
      them, for the fund appropriated for paying them as Rangers is exhausted;
      they will now receive 8d. a day and a suit of clothes as soon as they
      arrive, without paying for them.”
    </blockquote>
    <h6>LT GOV Dinwiddie to COL George Washington, 19 August 1756</h6>
    <blockquote>
      “I am sorry the Rangers seem to dislike the Service so much, but am still
      in hopes, the encouragement given by the committee will have some weight
      with them. They have allowed our Soldiers 8d. per day, without deductions
      for clothes or Surgeon: A General Hospital is established for their
      reception in case of accidents. The Clothes which are now given them,
      besides the 8d. per day, will be as good as any Soldiers in Europe. All
      these advantages they may enjoy, if they will only embrace this
      opportunity of enlisting.”
    </blockquote>
    <h6>COL Washington to CPT David Bell at Winchester, 6 September, 1756</h6>
    <blockquote>
      “I have ordered Stalniker to raise a Co. of Rangers and build a small
      stockade fort at Drapers Meadow.”( Smithfield, VA)
    </blockquote>
    <h6>LT GOV Dinwiddie to CPT Hogg, 8 September 1756</h6>
    <p>
      <b>8 September 1756 – December 1756</b>
      CPT Stalniker’s Ranger Company (15 men as of 17 December).
      <br>
      <br>
      <b>18 September 1756</b> MAJ Lewis and CPT Overton’s Ranger Company
      returns to Augusta County
    </p>
    <blockquote>
      "I promised, at their particular request, to address your Honor and the
      Assembly in their behalf, and that a regular force may be established in
      lieu of the militia and ranging companies, which are of much less service,
      and infinitely more cost to the country. Were this done, the whole would
      be under one direction, and any misbehavior could never pass with
      impunity. Whereas the others are soldiers at will, and in fact will go and
      come when and where they please, without regarding the orders or
      directions of any."
    </blockquote>
    <h6>COL Washington at Winchester to GOV Dinwiddie, 9 November, 1756.</h6>
    <p>
      <b>23 November 1756</b> MAJ Andrew Lewis orders CPT William Preston to
      attend militia draft at Robert Craven’s house and proceed with 60 men to
      Fort Miller (Vanderpool, VA) to relieve militia there.
      <br>
      <br>On
      <b>24 November 1756</b> the House of Burgesses voted to raise
      three Companies of Rangers with two stationed in Augusta County. MAJ
      Andrew Lewis was to command all the Rangers and one Company with CPT Peter
      Hogg, CPT John Dickinson and CPT William Preston commanding the other
      three. The private men were to be paid twelve pence, about fifteen cents,
      a day, and find their own clothing.
      <br>
      <br>
      <b>Nov 1756 - 1758</b> CPT Peter Hogg’s Company of Rangers LT James Dunlap
      24 Men
      <br>
      <br>
      <b>1757</b> Major Andrew Lewis appointed County Lieutenant of Augusta
      County and commander of the County Militia
      <br>
      <br>
      <b>1757</b> CPT Edward Goodrich’s Ranger Company
      <br>
      <br>
      <b>March 1757</b> Fort at Bull Pasture built by CPT William Preston’s
      Ranger Company.
    </p>
    <blockquote>
      “Whereas it is judged necessary in this time of open war, that a number of
      forces should be raised and kept on foot, for vindicating the honor of his
      majesty's crown, and for the safety and defence of this dominion,
      amounting to fifteen hundred and seventy two men, including three
      companies of rangers, to consist of one hundred men each. ……An exact
      discipline be observed, and that the soldiers who shall mutiny or stir up
      sedition, or shall desert his majesty's service be brought to a more
      exemplary and speedy punishment, than the usual forms of the law will
      allow……. That from and after the passing of this act, if any person being
      mustered, or in pay as an officer, or who is or shall be enlisted or in
      pay as a soldier or ranger, by virtue of any act of Assembly, or shall
      during the continuance of this act voluntarily enter himself into his
      majesty's service as a soldier or ranger, shall at any time during such
      continuance of this act within this dominion, begin, excite, cause, or
      join in any mutiny or sedition in the company or regiment whereto he doth
      belong, or in any other company, or desert his majesty's service, or being
      a soldier or ranger actually enlisted in any company, shall enlist himself
      in any other company, without a discharge produced in writing from the
      colonel, or in his absence the chief officer commanding the regiment or
      company in which he last served as an enlisted soldier or ranger; or if
      any officer, soldier or ranger, so enlisted as aforesaid, shall hold
      correspondence with any of his majesty's enemies, or give them advice or
      intelligence either by letters, messages, signs, or tokens, or any manner
      of way whatsoever, or shall treat with such enemies, or enter into any
      condition with them, without the license of his majesty's
      lieutenant-governor, or commander in chief of this dominion, or the
      colonel or chief officer commanding such regiment; or shall strike or use
      any violence against his superior officer, being in the execution of his
      office, or shall refuse to obey any lawful command of his superior
      officer, all and every person and persons so offending, in any of the
      matters before mentioned, shall suffer death, or such other punishment, as
      by a court martial shall be inflicted That the governor or commander in
      chief of this dominion, may from time to time grant a commission under the
      seal of this colony to any officer of such regiment, not under the degree
      of a field officer, for holding a general court martial within this
      dominion, for the trial of any officer or soldier belonging to the
      Virginia regiment, in which court martial all the offences above
      mentioned, and all other offences herein after specified shall be tried
      and proceeded against in such manner, as by this act shall be hereafter
      directed.”
    </blockquote>
    <h6>
      An act for Preventing Mutiny and Desertion,
      <br>Laws of Virginia, April 1757
    </h6>
    <blockquote>
      “A sum of money not to exceed six thousand pounds, to be laid out for and
      in the raising and maintaining three companies of men, consisting of fifty
      men each, with their officers, to be employed as rangers, for the
      protection of the subjects on the south west frontiers of this colony, as
      the governor shall direct from time to time, and shall not be sent out of
      this colony, nor incorporated with the soldiers now in his majesty’s
      service.”
    </blockquote>
    <h6>Laws of Virginia, April 1757</h6>
    <p>
      <b>8 June 1757</b> 3 Companies of Rangers of 100 men each to be raised for
      the southwest counties
      <br>
      <br>
      <b>8 June 1757 - 4 May 1759</b> CPT William Preston’s Company of Rangers
      29 Men
      <br>
      <br>
      <b>8 June 1757 - 1758</b> CPT John Ashby’s Company of Rangers
    </p>
    <blockquote>
      “And as you observe the absolute necessity of having a Company of Rangers,
      I agree to the raising sixty, seventy or 80 men to be commanded. by Mr.
      Rutherford, but you must be certain of his raising the men, not to load
      the country with a charge, as formerly, without men to the different
      Companies;--I do not doubt of your. keeping them strictly to their
      duty--his pay, with first and second Lieutenants to be the same as the
      Officers in Your Regiment and as the private Men are to have 12d. per day
      they are to have no enlisting money or clothing, and if possible they are
      to furnish their own arms, but if they cannot you are to supply them by
      delivering a number to Captain. Rutherford, on his receipt to restore
      them, casualties excepted; and they are to be provided with provisions by
      the Contractor; this I hope will encourage the settlers to remain on their
      Plantations. Your Care in having proper Lieutenants. for Captain
      Rutherford will be for the Public Service.”
    </blockquote>
    <h6>GOV Dinwiddie to COL Washington, 2 Nov 1757</h6>
    <p>
      <b>4 August 1757</b>
      CPT Peter Hogg relieved of command and CPT Peter Hogg’s Ranger Company to
      be commanded by MAJ Andrew Lewis
      <br>
      <br>
      <b>1758-59</b> CPT Robert Rutherford’s Company of Rangers
      <br>
      <br>
      <b>1758</b> CPT Robert Wade’s Ranger Company
      <br>
      <br>
      <b>1758</b> CPT James Dunlop’s Company of Rangers
      <br>
      <br>
      <b>30 March 1758</b> Colonial Assembly provides for 4 Companies of Rangers
      and instructs militia to garrison forts left by Virginia Regiment
    </p>
    <blockquote>
      “Captain Rutherford’s company was raised and posted on this quarter by
      Governor Dinwiddie’s express order and can be more useful here, than any
      other men whatever, being all sons of the neighboring farmers, men of
      property, young, active and entirely acquainted with the woods of these
      frontiers. Whereas, if they go southward they will be utter strangers to
      the enemy’s haunts, and of no more use there than the militia of an
      adjacent county; while their places must there be supplied with militia
      equally ignorant of these woods as they will be of any others, besides
      giving them a useless march of two hundred miles, and exposing the
      frontiers in the mean time. Another reason may be urged; their property
      lies in this county. Interested motives induced them to enlist, and to be
      vigilant in defending it, and, I believe, they would desert rather than go
      to the southward.”
    </blockquote>
    <h6>
      COL Washington at Ft. Loudoun to the President of the Council,
      <br>24 April 1758
    </h6>
    <blockquote>
      “I humbly conceive therefore, that it would be infinitely more for the
      interest of the service, to order the 100 from Prince William to the South
      Branch, and continuing Rutherford’s Company in its present station, making
      this its headquarters. For, as that company is perfectly acquainted with
      all that range of mountains, extending from the Potomac to the Augusta
      Line, and through which the enemy make incursions into this settlement,
      they could with greater facility obstruct their inroads and assist the
      inhabitants of this valley (of whom they themselves form a very great
      part) than those who are ignorant of the ground.”
    </blockquote>
    <h6>
      COL Washington at Ft. Loudoun to the President of the Council,
      <br>4 May 1758
    </h6>
    <blockquote>
      “I have been waiting ‘till now for Capt. Rutherford’s pay-roll; his
      company being much dispersed in the Ranging Service.”
    </blockquote>
    <h6>
      COL Washington at Ft. Loudoun to Lieutenant Governor Fauquier,
      <br>19 June 1758
    </h6>
    <blockquote>
      “A sum of not to exceed four thousand pounds, to be laid out for and in
      the raising and maintaining four companies of rangers, consisting of one
      hundred men each, with their officers, to be employed as rangers, for the
      protection of the subjects on the south west frontiers of this colony, as
      the governor shall direct from time to time, and shall not be sent out of
      this colony.”
    </blockquote>
    <h6>Laws of Virginia, September 1758</h6>
    <p>
      <b>14 September 1758</b> CPT John Dickenson’s Company of Militia Rangers
      at Fort Young (Covington City, VA).
      <br>
      <br>
      <b>February 1759 - February 1760</b> Virginia Frontier Battalion under LTC
      William Peachy w/LT Benjamin Temple with a planned strength of 500 men.
      <br>
      <br>
      <b>February - December 1759</b> 4 Ranger Companies in service
    </p>
    <blockquote>
      “The Burgesses yesterday passed a bill to raise pay and clothe one
      Regiment of 1000 private men complete, which I have the power to march out
      of the Colony to join the rest of His Majesty’s Forces to act offensively
      against the enemy. These shall be raised and ordered to march to the
      General Rendezvous with the greatest expedition possible. They have raised
      a body of 500, on a different establishment, to remain home to protect the
      frontiers.”
    </blockquote>
    <h6>LT GOV Fauquier to GEN Amherst, 3 April 1759</h6>
    <blockquote>
      “Mr. Harrison informed the House, that he had, according to Order, waited
      on his Honor the Governor with the Address to of this House, desiring that
      he would be pleased to give Orders for the disbanding the Rangers on the
      Frontier of this colony, to which his Honor was pleased to answer he would
      comply with the desire of the House as soon as possible”
    </blockquote>
    <h6>Journal of the House of Burgesses, 6 April 1759</h6>
    <p>
      <b>14 June 175</b> William Preston promoted from CPT to LTC of the Augusta
      Militia.
      <br>
      <br>
      <b>October 1759</b> Indian raid on settlement at Kerr’s Creek. 12 KIA and
      13 captured.
      <br>
      <br>Pursued by CPT Charles Lewis and CPT Dickenson’s Ranger Companies
      killing 20 and recovering POWs (Straight Fork west of Crab Bottom in
      Highland County).
      <br>
      <br>
      <b>30 November 1759</b> Virginia Gazette. Announcement of and reward for
      the apprehension of two deserters from the Virginia Frontier Battalion at
      Staunton by LTC William Peachy.
      <br>
      <br>
      <b>1760-64</b> CPT William Christian’s Company of Rangers (11 Men).
      <br>
      <br>
      <b>1763</b> CPT William Phillips Company of Rangers (27 Men).
    </p>
    <blockquote>
      “The militia are called out merely to protect the inhabitants of the back
      settlements, and can be employed to no other purpose. Colonel Stephen now
      has 250 men in small posts in the Counties of Frederick and Hampshire on
      the waters of the Potomac which divides the neighboring colonies of
      Maryland and Pennsylvania from this. Their chief use is to pursue any
      parties of Indians who make irruptions into the colony and in that
      capacity have done execution since last summer. They are not marched
      farther west than the Allegheny Mountains, it not being thought advisable
      that they should go further out of the settlements. Colonel Lewis has 450
      men to the southward of Colonel Stephen on the same plan having the
      defense of the frontiers from Lord Fairfax’s to the Carolina Line under
      his charge. The Officers cannot by law be under any orders but my own.”
    </blockquote>
    <h6>LT GOV Fauquier to COL Bouquet, 19 April 1764</h6>
    <hr>
    <h3>Sources:</h3>
    <ol>
      <li>
        Burton, Agnes, ed. “Collections and Researches Made by the Michigan
        Pioneer and Historical Society, Bouquet Papers, VOL XIX Wynkoop,
        Hallenbeck Crawford Co. Lansing, MI, 1911
      </li>
      <li>
        Clark, Murtie J., “Colonial Soldiers of the South, 1732-1744”,
        Genealogical Publishing,
      </li>
      <li>
        Hamilton, Stanislaus, ed., “Letters to Washington, Vol. II, 1756-1758”,
        Houghton, Mifflin and Co., Cambridge, 1899
      </li>
      <li>
        Ford, Worthington C., “The Writings of George Washington,” Vol. II,
        1758-1775, G.P. Putnam and Sons, New York, 1889
      </li>
      <li>
        <a href="http://www.vagenweb.org/hening/">
          Hening, William Walter, “Hening’s Statutes at Large, Being a
          Collection of all the Laws of Virginia from the first session of the
          Legislature, in the Year 1619.”
        </a>
      </li>
      <li>
        Johnson, David E., “A History of Middle New River Settlements and
        Contiguous Territory”, Standard Printing and Publishing, Huntington, WV,
        1906
      </li>
      <li>
        Kennedy, John, ed., ‘Journals of the House of Burgesses of Virginia,
        1761-1765, Richmond, VA, 1908
      </li>
      <li>
        Kercheval, Samuel, “A History of the Valley of Virginia”, John Gatewood,
        Printer, Woodstock, VA, 1850
      </li>
      <li>
        Kimball, Gertrude, ed., “Correspondence of William Pitt, Vol. I. and
        Vol. II , MacMillan Company, New York, 1906
      </li>
      <li>
        Koontz, Louis, “The Virginia Frontier, 1754-1763”, Johns Hopkins Press,
        Baltimore, 1925
      </li>
      <li>
        Lewis, Virgil, “First Biennial Report of the Department of Archives and
        History of the State of West Virginia,” Tribune Printing Company,
        Charleston, West Virginia, 1906
      </li>
      <li>
        Mc Ilwaine, H.R., ed., ‘Journals of the House of Burgesses of Virginia,
        1758-1761, Richmond, VA, 1908
      </li>
      <li>
        Morton, Oren, “Annals of Bath County Virginia,” Mc Clure Company,
        Staunton, VA 1917
      </li>
      <li>
        Osborn, Richard “William Preston in the American Revolution”, Journal of
        Backcountry Studies, Vol. III, Issue I, Winter/Spring 2008
      </li>
      <li>
        Reese, George, ed. “The Official Papers of Francis Fauquier, Lieutenant
        Governor of Virginia, 1758-1768, Vols. I-III, University Press of
        Virginia, Charlottesville, VA, 1980
      </li>
      <li>
        Sparks, Jared, ed., “Writings of George Washington”, Vol. II, Little
        Brown, and Co., Boston 1855
      </li>
      <li>
        Titus, James W.” Soldiers When They Chose To Be So: Virginians at War
        1754-1763,” Doctoral Thesis, Rutgers University, New Brunswick, NJ, 1883
      </li>
      <li>
        Washington, George, 1732-1799. The writings of George Washington from
        the original manuscript sources: volume 1 Electronic Text Center,
        University of Virginia Library
      </li>
      <li>
        The Official Records of Robert Dinwiddie, Vol.’s I and II, Virginia
        Historical Society Richmond, VA, 1884
      </li>
      <li>
        <a href="https://research.history.org/DigitalLibrary/va-gazettes/">Virginia Gazette</a>
      </li>
    </ol>
  </div>
</template>

<script>
export default {};
</script>

<style lang="scss" scoped></style>
